import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import SliderHandle from './SliderHandle';
import { ContentComparisonSliderSC as T } from './ContentComparisonSliderSC';
import { Button, Icon } from 'imaginarity-react-ui';

interface ContentComparisonSliderProps {
    contentOne: React.ReactNode;
    contentTwo: React.ReactNode;
    orientation?: 'horizontal' | 'vertical';
    aspectRatio: number;
    allowSlideByMouseOver?: boolean;
    allowSlideByGrab?: boolean;
    allowSlideByClick?: boolean;
    allowPointerEvents?: boolean;
    infoText?: string;
    infoTextColor?: string;
    infoTextColorShadow?: string;
    noContextMenu?: boolean;
}

const edgeThresholdPx = 25;
const animationDurationMs = 200;

const ContentComparisonSlider = (p: ContentComparisonSliderProps) => {
    const { contentOne, contentTwo, orientation = 'vertical', aspectRatio, allowSlideByMouseOver = true, allowSlideByGrab = false, allowSlideByClick = false, allowPointerEvents = false, infoText, infoTextColor, infoTextColorShadow, noContextMenu = false } = p;
    const [sliderPosition, setSliderPosition] = useState(50);
    const [isDragging, setIsDragging] = useState(false);
    const [containerHeight, setContainerHeight] = useState<number | 'auto'>('auto');
    const [isAnimating, setIsAnimating] = useState(false);
    const [contextMenuVisible, setContextMenuVisible] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
    const [pointerEventsEnabled, setPointerEventsEnabled] = useState(allowPointerEvents);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleWindowMouseUp = () => {
            if (isDragging) {
                setIsDragging(false);
            }
        };
        window.addEventListener('mouseup', handleWindowMouseUp);
        return () => {
            window.removeEventListener('mouseup', handleWindowMouseUp);
        };
    }, [isDragging]);

    useEffect(() => {
        const calculateHeight = () => {
            if (containerRef.current) {
                setContainerHeight(containerRef.current.offsetWidth / aspectRatio);
            }
        };
        const resizeObserver = new ResizeObserver(() => calculateHeight());
        if (containerRef.current) resizeObserver.observe(containerRef.current);

        return () => resizeObserver.disconnect();
    }, [aspectRatio]);

    useEffect(() => {
        if (noContextMenu === false)
            if (!allowPointerEvents) setPointerEventsEnabled(false);
        if (noContextMenu === true)
            setPointerEventsEnabled(allowPointerEvents);
    }, [allowPointerEvents, noContextMenu]);



    useEffect(() => {
        const updateContextMenuPosition = (e: MouseEvent) => {
            const distance = Math.sqrt(
                (e.clientX - contextMenuPosition.x) ** 2 + (e.clientY - contextMenuPosition.y) ** 2
            );
            if (contextMenuVisible && distance > 200) {
                setContextMenuPosition({ x: e.clientX, y: e.clientY });
            }
        };

        if (contextMenuVisible) {
            window.addEventListener('mousemove', updateContextMenuPosition);
        } else {
            window.removeEventListener('mousemove', updateContextMenuPosition);
        }

        return () => window.removeEventListener('mousemove', updateContextMenuPosition);
    }, [contextMenuVisible, contextMenuPosition]);

    // Animationsstatus zurücksetzen
    useEffect(() => {
        if (isAnimating) {
            const timeout = setTimeout(() => setIsAnimating(false), animationDurationMs);
            return () => clearTimeout(timeout);
        }
    }, [isAnimating]);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (contextMenuVisible && containerRef.current) {
                const container = containerRef.current.getBoundingClientRect();
                const isOutside =
                    e.clientX < container.left ||
                    e.clientX > container.right ||
                    e.clientY < container.top ||
                    e.clientY > container.bottom;

                if (isOutside) {
                    setContextMenuVisible(false);
                }
            }
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, [contextMenuVisible]);


    const updateSliderPosition = useCallback(
        (clientX: number, clientY: number) => {
            if (!containerRef.current) return;
            const container = containerRef.current.getBoundingClientRect();
            const thresholdPercentage =
                orientation === 'vertical'
                    ? (edgeThresholdPx / container.width) * 100
                    : (edgeThresholdPx / container.height) * 100;

            let newSliderPosition =
                orientation === 'vertical'
                    ? ((clientX - container.left) / container.width) * 100
                    : ((clientY - container.top) / container.height) * 100;

            if (newSliderPosition < thresholdPercentage) {
                newSliderPosition = 0;
                setIsAnimating(true);
            } else if (newSliderPosition > 100 - thresholdPercentage) {
                newSliderPosition = 100;
                setIsAnimating(true);
            }

            setSliderPosition(newSliderPosition);
        },
        [orientation]
    );

    const handleMouseDown = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (e.button === 0 && allowSlideByGrab) {
                setIsDragging(true);
                updateSliderPosition(e.clientX, e.clientY);
            }
        },
        [allowSlideByGrab, updateSliderPosition]
    );

    const handleMouseMove = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (isDragging) {
                updateSliderPosition(e.clientX, e.clientY);
            } else if (allowSlideByMouseOver) {
                updateSliderPosition(e.clientX, e.clientY);
            }
        },
        [allowSlideByMouseOver, isDragging, updateSliderPosition]
    );

    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (allowSlideByClick) updateSliderPosition(e.clientX, e.clientY);
        },
        [allowSlideByClick, updateSliderPosition]
    );

    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        setContextMenuVisible(!contextMenuVisible);
        if (!contextMenuVisible) setContextMenuPosition({ x: e.clientX, y: e.clientY });
    };

    const togglePointerEvents = () => {
        setPointerEventsEnabled((prev) => !prev);
        setContextMenuVisible(false);
    };

    const closeContextMenu = () => setContextMenuVisible(false);

    const showSlider = useMemo(() => sliderPosition > 1 && sliderPosition < 99, [sliderPosition]);

    return (
        <>
            <T.ContentComparisonSliderContainer
                ref={containerRef}
                style={{ height: containerHeight }}
                onMouseMove={pointerEventsEnabled ? undefined : handleMouseMove}
                onMouseDown={pointerEventsEnabled ? undefined : handleMouseDown}
                onClick={pointerEventsEnabled ? undefined : handleClick}
                onContextMenu={noContextMenu ? undefined : (allowPointerEvents ? handleContextMenu : undefined)}
                orientation={orientation}
                allowPointerEvents={pointerEventsEnabled}
                isLoaded
            >
                <T.ContainerBottom
                    allowPointerEvents={pointerEventsEnabled}
                >
                    {contentOne}
                </T.ContainerBottom>
                <T.ContainerTop
                    orientation={orientation}
                    sliderPosition={sliderPosition}
                    isAnimating={isAnimating}
                    animationDuration={`${animationDurationMs}ms`}
                    allowPointerEvents={pointerEventsEnabled}
                >
                    {contentTwo}
                </T.ContainerTop>

                {showSlider && (
                    <SliderHandle
                        orientation={orientation}
                        allowSlideByGrab={allowSlideByGrab}
                        handleMouseDown={handleMouseDown}
                        sliderPosition={sliderPosition}
                        locked={pointerEventsEnabled}
                        onClick={togglePointerEvents}
                        noContextMenu={noContextMenu}
                    />
                )}

                {infoText && <T.InfoText infoTextColor={infoTextColor} infoTextColorShadow={infoTextColorShadow}>{infoText}</T.InfoText>}
            </T.ContentComparisonSliderContainer>

            {contextMenuVisible && (
                <T.ContextMenu style={{ top: contextMenuPosition.y, left: contextMenuPosition.x, transform: "translate(-60px, -20px)", boxShadow: "0 6px 8px -6px #000, 0 0 50px 8px #fff" }}>
                    <T.ContextMenuItemCancel>
                        <Icon name="times" />
                    </T.ContextMenuItemCancel>
                    <T.ContextMenuItemCancel>
                        <Button
                            kind="transparentButton"
                            fluid
                            onClick={closeContextMenu}
                            content="Cancel"
                        />
                    </T.ContextMenuItemCancel>
                    <T.ContextMenuItem onClick={togglePointerEvents}>
                        <Icon name={pointerEventsEnabled ? "unlock" : "lock"} />
                    </T.ContextMenuItem>
                    <T.ContextMenuItem>
                        <Button
                            kind="transparentButton"
                            fluid
                            onClick={togglePointerEvents}
                            content={pointerEventsEnabled ? 'Disable Pointer Events' : 'Enable Pointer Events'}
                        />
                    </T.ContextMenuItem>
                </T.ContextMenu>
            )}
        </>
    );
};

export default ContentComparisonSlider;
